import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isAfter,
  isSameDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { Subscription } from 'rxjs';
import { enterAndLeave } from '../../common-animation';
import { AMatCalendarHeaderComponent } from '../a-mat-calendar-header/a-mat-calendar-header.component';
import { GenerateDateByCode } from '../../utils/utils';

@Component({
  selector: 'app-a-date-range-slider',
  templateUrl: './a-date-range-slider.component.html',
  styleUrls: ['./a-date-range-slider.component.scss'],
  animations: [enterAndLeave],
})
export class ADateRangeSliderComponent implements OnInit, OnChanges {
  startDateItemOptions = [
    {
      text: 'Inception to Date',
      value: 'startOfITD',
    },
    {
      text: 'Start of Month',
      value: 'startOfMonth',
    },
    {
      text: 'Start of Quarter',
      value: 'startOfQuarter',
    },
    {
      text: 'Start of Year',
      value: 'startOfYear',
    },
  ];
  endDateItemOptions = [
    {
      text: 'Today',
      value: 'today',
    },
    // {
    //     text: 'Last Data Point',
    //     value: 2,
    // },
    {
      text: 'End of Month',
      value: 'endOfMonth',
    },
    {
      text: 'End of Quarter',
      value: 'endOfQuarter',
    },
    {
      text: 'End of Year',
      value: 'endOfYear',
    },
  ];
  @Input() dateScopeStart!: Date;
  @Input() dateScopeEnd!: Date;
  @Input() format: string = 'yyyy/MM/dd';

  @ViewChild('endDateDatePicker', { static: false })
  endDateDatePicker: MatCalendar<Date>;
  @ViewChild('startDateDatePicker', { static: false })
  startDateDatePicker: MatCalendar<Date>;

  @Output() onDateRangeChange: EventEmitter<{
    start: Date;
    end: Date;
    startType: string;
    endType: string;
  }> = new EventEmitter();

  allDates: Date[] = [];

  @Input('startType') initialStartType: any;
  @Input('endType') initialEndType: any;

  selectedStartOption: any;
  selectedEndOption: any;

  customDateStart?: Date;
  customDateEnd?: Date;

  startDate?: Date;
  endDate?: Date;

  @Input('start') initialStartDate?: Date;
  @Input('end') initialEndDate?: Date;

  startPercentage: number = 0;
  endPercentage: number = 100;

  isStartOpen = false;
  isEndOpen = false;

  headerComponent = AMatCalendarHeaderComponent;
  scrollStrategy: any;

  itdStartDate = new Date('2000/01/01');

  overlayPosition = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 0,
    },
  ];

  unsubscription: Subscription = new Subscription();

  constructor(
    private cd: ChangeDetectorRef,
    private sstrategy: ScrollStrategyOptions
  ) {
    this.scrollStrategy = this.sstrategy.block();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['initialStartDate'] &&
      !isSameDay(
        changes['initialStartDate'].currentValue,
        changes['initialStartDate'].previousValue
      )
    ) {
      this.initStartDateAndDropdown(
        this.initialStartDate,
        changes['initialStartDate'].firstChange
          ? this.initialStartType
          : changes['initialStartType']
          ? changes['initialStartType'].currentValue
          : false
      );
    }
    if (
      changes['initialEndDate'] &&
      !isSameDay(
        changes['initialEndDate'].currentValue,
        changes['initialEndDate'].previousValue
      )
    ) {
      this.initEndDateAndDropdown(
        this.initialEndDate,
        changes['initialEndDate'].firstChange
          ? this.initialEndType
          : changes['initialEndDate']
          ? changes['initialEndDate'].currentValue
          : false
      );
    }

    // if (
    //   changes['initialStartType'] &&
    //   !changes['initialStartType'].currentValue
    // ) {
    //   this.customDateStart = this.initialStartDate;
    // }

    // if (changes['initialEndType'] && !changes['initialEndType'].currentValue) {
    //   this.customDateEnd = this.initialEndDate;
    // }
  }

  ngOnInit(): void {
    if (!this.dateScopeStart) {
      this.dateScopeStart = this.itdStartDate;
    } else {
      // make sure that the minimum date scope is between start of ITD and the end of the current year.
      if (isAfter(this.dateScopeStart, this.itdStartDate)) {
        this.dateScopeStart = this.itdStartDate;
      }
    }
    if (!this.dateScopeEnd) {
      this.dateScopeEnd = endOfYear(new Date());
    } else {
      // make sure that the minimum date scope is between start of ITD and the end of the current year.
      if (isAfter(endOfYear(new Date()), this.dateScopeEnd)) {
        this.dateScopeEnd = endOfYear(new Date());
      }
    }
    this.selectedStartOption = this.initialStartType;
    this.selectedEndOption = this.initialEndType;
    this.calculateAllDates();
    if (this.initialStartDate) {
      this.initStartDateAndDropdown(
        this.initialStartDate,
        this.initialStartType
      );
    } else {
      this.startDate = this.dateScopeStart;
    }
    if (this.initialEndDate) {
      this.initEndDateAndDropdown(this.initialEndDate, this.initialEndType);
    } else {
      let now = new Date();
      now.setHours(0, 0, 0);
      if (now < this.dateScopeEnd) {
        this.endDate = now;
      } else {
        this.endDate = this.dateScopeEnd;
      }

      this.initEndDateAndDropdown(this.endDate);
    }
  }

  onStartDateItemChange() {
    this.customDateStart = undefined;
    this.startDate = this.generateDateByCode(this.selectedStartOption);
    this.startPercentage = this.calculateFromDateToPercentage(this.startDate);
    if (isAfter(this.startDate, this.endDate)) {
      this.customDateEnd = this.startDate;
      this.endDate = this.startDate;
      this.endPercentage = this.startPercentage;
    }
    this.emitDateRangeChange();
  }

  onEndDateItemChange() {
    this.customDateEnd = undefined;
    this.endDate = this.generateDateByCode(this.selectedEndOption);
    this.endPercentage = this.calculateFromDateToPercentage(this.endDate);
    if (isAfter(this.startDate, this.endDate)) {
      this.customDateStart = this.endDate;
      this.startDate = this.endDate;
      this.startPercentage = this.endPercentage;
    }
    this.emitDateRangeChange();
  }

  onCustomDateSocpeStartChange(event: any) {
    setTimeout(() => {
      this.selectedStartOption = '';
      //this.overlayOutsideClick();
      this.isStartOpen = false;
      this.startPercentage = this.calculateFromDateToPercentage(
        this.customDateStart
      );
      this.startDate = this.customDateStart;
      this.cd.detectChanges();
      this.emitDateRangeChange();
    }, 0);
  }
  onCustomDateSocpeEndChange(event: any) {
    setTimeout(() => {
      this.selectedEndOption = '';
      // this.overlayOutsideClick();
      this.isEndOpen = false;
      this.endPercentage = this.calculateFromDateToPercentage(
        this.customDateEnd
      );
      this.endDate = this.customDateEnd;
      this.cd.detectChanges();
      this.emitDateRangeChange();
    }, 0);
  }

  onDateSliderChange(event: any) {
    this.startPercentage = event.leftPercentage;
    this.endPercentage = event.rightPercentage;

    this.calculateFromPercentageToDate();
    if (event.targetName === 'left') {
      this.selectedStartOption = '';
      //this.customDateStart = this.startDate;
      this._calculateStartDropdown(this.startDate);
    } else if (event.targetName === 'right') {
      this.selectedEndOption = '';
      //this.customDateEnd = this.endDate;
      this._calculateEndDropdown(this.endDate);
    }
    setTimeout(() => {
      if (this.endDateDatePicker) {
        this.endDateDatePicker.activeDate = this.customDateEnd
          ? this.customDateEnd
          : this.endDate;
      }
      if (this.startDateDatePicker) {
        this.startDateDatePicker.activeDate = this.customDateStart
          ? this.customDateStart
          : this.startDate;
      }
      if (this.startDateDatePicker || this.endDateDatePicker) {
        this.cd.detectChanges();
      }
    }, 300);
    this.cd.detectChanges();
    this.emitDateRangeChange();
  }

  calculateAllDates() {
    this.allDates = eachDayOfInterval({
      start: this.dateScopeStart,
      end: this.dateScopeEnd,
    });
  }
  calculateFromPercentageToDate() {
    //replace the Math.floor to Math.round to fix '#120149 bug QA bug - IP: 31st day is saved as 30th for some months in the slider' bug
    const startIndex = Math.round(
      (this.allDates.length - 1) * (this.startPercentage / 100)
    );
    const endIndex = Math.round(
      (this.allDates.length - 1) * (this.endPercentage / 100)
    );
    this.startDate = this.allDates[startIndex];
    this.endDate = this.allDates[endIndex];
  }

  calculateFromDateToPercentage(value: Date) {
    const index = this.allDates.findIndex((v) => isSameDay(v, value));
    return (index / (this.allDates.length - 1)) * 100;
  }

  overlayOutsideClick() {
    this.isStartOpen = false;
    this.isEndOpen = false;
  }

  generateDateByCode(code: string) {
    return GenerateDateByCode(code);
  }

  _calculateStartDropdown(date: Date) {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    if (isSameDay(startOfMonth(today), date)) {
      this.selectedStartOption = 'startOfMonth';
    } else if (isSameDay(new Date('2000/01/01'), date)) {
      this.selectedStartOption = 'startOfITD';
    } else if (isSameDay(startOfQuarter(today), date)) {
      this.selectedStartOption = 'startOfQuarter';
    } else if (isSameDay(startOfYear(today), date)) {
      this.selectedStartOption = 'startOfYear';
    } else {
      this.selectedStartOption = '';
      this.customDateStart = date;
    }
  }

  initStartDateAndDropdown(date: Date, hasDefaultStartOption: boolean = false) {
    if (!hasDefaultStartOption) {
      this._calculateStartDropdown(date);
      this.startDate = date;
    } else {
      this.startDate = date;
      this.customDateStart = date;
      this.selectedStartOption = this.initialStartType;
    }
    this.startPercentage = this.calculateFromDateToPercentage(date);
  }

  _calculateEndDropdown(date: Date) {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    if (isSameDay(endOfMonth(today), date)) {
      this.selectedEndOption = 'endOfMonth';
    } else if (isSameDay(today, date)) {
      this.selectedEndOption = 'today';
    } else if (isSameDay(endOfQuarter(today), date)) {
      this.selectedEndOption = 'endOfQuarter';
    } else if (isSameDay(endOfYear(today), date)) {
      this.selectedEndOption = 'endOfYear';
    } else {
      this.selectedEndOption = '';
      this.customDateEnd = date;
    }
  }

  initEndDateAndDropdown(date: Date, hasDefaultEndOption: boolean = false) {
    if (!hasDefaultEndOption) {
      this._calculateEndDropdown(date);
      this.endDate = date;
    } else {
      this.endDate = date;
      this.customDateEnd = date;
      this.selectedEndOption = this.initialEndType;
    }
    this.endPercentage = this.calculateFromDateToPercentage(date);
  }
  emitDateRangeChange() {
    this.onDateRangeChange.emit({
      start: this.startDate,
      end: this.endDate,
      startType: this.selectedStartOption,
      endType: this.selectedEndOption,
    });
  }
}
